import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { getExceptionToastDescription } from "@shared/utils/errors/error-utils";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Box from "@ui-kit/layout/Box/Box.web";

import AllInPricing from "@shared/modules/financials/components/AllInPricing/AllInPricing.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import CartTimer from "@ui-application/data-display/CartTimer/CartTimer.web";
import DefaultModalContainer from "@/components/molecules/modals/DefaultModalContainer/DefaultModalContainer";
import ModalFooterButtons from "@/components/molecules/modals/Footer/ModalFooterButtons";
import { trackItemsInCart } from "@shared/modules/events/utils/detail-page-helpers";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import useAnalytics from "@shared/lib/analytics/hooks/useAnalytics";
import useDebounce from "@/hooks/useDebounce";
import useMediaQuery from "@/hooks/useMediaQuery";
import { logAppInfo, recordAppError } from "@/utils/errors/errorLogger";
import { breakpoints } from "@/styles/constants";
import { X_SP_UUID } from "@shared/env";
import { DISPLAY_QUANTITY_DROPDOWN_THRESHOLD, enums } from "@shared/constants";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";

import { useBreakpointValue } from "@shared/hooks/useBreakpointValue";
import VStack from "@ui-kit/layout/Stack/VStack.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useEffect, useMemo, useRef, useState } from "react";
import ReadMore from "@ui-kit/typography/ReadMore/ReadMore.web";
import { getItemPrice } from "@shared/modules/financials/utils/price-utils";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import CDNImage from "@ui-kit/media/CDNImage/CDNImage.web";
import ProductVariantSelect from "@ui-application/form/ProductVariantSelect/ProductVariantSelect.web";
import Form from "@ui-kit/form/Form/Form.web";

const notifier = NotifierFactory.createNotifier();

const { sm } = breakpoints;

export default function ProductModal({
  isOpen,
  onClose,
  selectedAttractionProduct,
  hasAllInPricing,
  venue,
  isBasketUpdating,
  dynamicHoldSlug = null,
  purchaseLimits = null,
  itemCount = 0,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedAttractionProduct: ProductTypeSerializer;
  hasAllInPricing: boolean;
  venue: NestedPublicVenueSerializer;
  isBasketUpdating: boolean;
  dynamicHoldSlug?: string;
  purchaseLimits?: {
    min: number;
    max: number;
  };
  itemCount?: number;
}) {
  const analytics = useAnalytics();
  const { t } = useTranslation("purchases");
  const {
    modifyItems,
    itemGroups,
    isExpired,
    paymentType,
    purchaseSourcePlatform,
  } = usePublicBasket();
  const [isSmallerThanSm] = useMediaQuery(`(max-width: ${sm - 1}px)`);
  const defaultImage = formatRelativeStaticPath(
    "images/default-showpass-square.png"
  );

  // initialize quantitySelected to 0 on the product_attributes object
  const formattedAndSortedProductVariants = useMemo(
    () =>
      selectedAttractionProduct?.product_attributes
        ?.sort((a, b) =>
          Number(a?.sort_order) < Number(b?.sort_order) ? -1 : 1
        )
        .map((variant) => {
          return {
            ...variant,
            quantitySelected: itemGroups.filter(
              ({ product_attribute }) => product_attribute === variant.id
            ).length,
          };
        }),
    [itemGroups, selectedAttractionProduct?.product_attributes]
  );

  // the currently-selected product variant on the variant dropdown
  // initialize to the first product variant
  const [selectedVariantInfo, setSelectedVariantInfo] = useState(
    formattedAndSortedProductVariants?.[0]
  );

  const totalVariantsQuantity = useMemo(
    () =>
      itemGroups
        .filter(
          ({ product_attribute }) =>
            product_attribute === selectedVariantInfo?.id
        )
        .reduce((prev, curr) => prev + (curr?.quantity || 0), 0),
    [itemGroups, selectedVariantInfo?.id]
  );

  // This controls the quantity amount requested to the API, changed by this component only.
  const [amountRequested, setAmountRequested] = useState({
    amount: totalVariantsQuantity,
  });

  // This controls the quantity amount displayed to the user, changed by this component and by the basket.
  const [amountSelectedDisplay, setAmountSelectedDisplay] = useState(
    totalVariantsQuantity
  );

  const [debouncedAmountRequested] = useDebounce(
    amountRequested,
    selectedVariantInfo?.purchase_limit > DISPLAY_QUANTITY_DROPDOWN_THRESHOLD
      ? 0
      : 500 // no debounce when the quantity dropdown is displayed
  );

  const allowRequest = useRef(true);
  const componentDidMount = useRef(false);

  const quantityButtonsDataTestId = useBreakpointValue({
    base: `${selectedVariantInfo?.name}-quantity-select-below`,
    md: `${selectedVariantInfo?.name}-quantity-select-same-line`,
  });

  useEffect(() => {
    // Change the displayed quantity when the basket quantity changes.
    setAmountSelectedDisplay(totalVariantsQuantity);
  }, [totalVariantsQuantity]);

  useEffect(() => {
    // Change the displayed quantity when this component requests a change.
    if (allowRequest.current) {
      setAmountSelectedDisplay(amountRequested.amount ?? 0);
    }
  }, [amountRequested]);

  const handleAmountSelected = (amount: number) => {
    setAmountRequested({ amount });
  };

  useEffect(() => {
    if (!componentDidMount.current) {
      // do nothing on the first component render
      componentDidMount.current = true;
      return;
    }

    // prevent this api call from firing automatically when the cart expires and item amount is reset to 0
    if (allowRequest.current) {
      modifyItems(
        {
          item: selectedVariantInfo,
          quantity: debouncedAmountRequested.amount,
        },
        { hold_slug: dynamicHoldSlug }
      )
        .then((returnedBasket) => {
          const spAddToCartCall = () => {
            UserInteractionLogger.addToCart({
              item: {
                item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id,
                type: selectedVariantInfo?.id,
              },
              count: debouncedAmountRequested.amount,
              venue: venue.name,
              basket: returnedBasket,
              product: selectedAttractionProduct,
              productVariant: selectedVariantInfo,
            });
          };

          trackItemsInCart(
            debouncedAmountRequested.amount,
            returnedBasket,
            {
              itemId: selectedVariantInfo?.id,
              itemName: selectedAttractionProduct.name,
              itemVariant: selectedVariantInfo?.name,
              itemType: enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id,
            },
            venue,
            analytics,
            spAddToCartCall,
            { uuid: returnedBasket[X_SP_UUID] }
          );
        })
        .catch((err) => {
          // On error, revert displayed amount to basket amount
          setAmountSelectedDisplay(totalVariantsQuantity);
          allowRequest.current = false;
          setAmountRequested({ amount: totalVariantsQuantity });

          notifier.error(
            getExceptionToastDescription(err) ?? t("Unknown error")
          );
          logAppInfo("Error changing product quantity");
          recordAppError(err);
        });
    } else {
      allowRequest.current = true;
    }
  }, [debouncedAmountRequested]); //eslint-disable-line

  // set amount on ticket card to 0 when cart expires
  // re-initialize cart
  useEffect(() => {
    if (isExpired) {
      allowRequest.current = false;
      setAmountRequested({ amount: 0 });
    }
  }, [isExpired]);

  const priceComponent: JSX.Element = (
    <Flex
      pt={{ base: "2xl", sm: "none" }}
      pb={{ base: "2xl", sm: "md" }}
      direction="column"
      justifyContent="space-between"
      width="100%"
    >
      <Text pb="xs" fontSize={{ base: "3xl", sm: "lg" }} fontWeight="bold">
        {selectedAttractionProduct.name}
      </Text>
      <Text fontSize={{ base: "lg", sm: "md" }} fontWeight="bold">
        <CurrencyFormat
          value={getItemPrice(
            selectedVariantInfo?.price,
            hasAllInPricing,
            selectedVariantInfo?.fees_pricing_info,
            paymentType,
            purchaseSourcePlatform
          )}
          displayType="text"
          currency={selectedAttractionProduct.currency}
        />
        {!!selectedVariantInfo?.price &&
          ` ${selectedAttractionProduct.currency}`}
      </Text>

      <AllInPricing
        hasAllInPricing={hasAllInPricing}
        feesPricingInfo={selectedVariantInfo?.fees_pricing_info}
        price={selectedVariantInfo?.price}
        currency={selectedAttractionProduct?.currency}
        purchaseSourcePlatform={purchaseSourcePlatform}
        paymentType={paymentType}
      />
    </Flex>
  );

  const readMoreComponent: JSX.Element =
    selectedAttractionProduct.description && (
      <Flex width="100%">
        <ReadMore
          openReadMoreText={t("Read More")}
          closeReadMoreText={t("Show Less")}
          descriptionHtml={selectedAttractionProduct.description}
        />
      </Flex>
    );

  return (
    <DefaultModalContainer
      title={t("Product Selection")}
      isOpen={isOpen}
      onClose={onClose}
      footerContainer={
        <ModalFooterButtons
          venue={venue}
          onClose={onClose}
          isDynamicHold={!!dynamicHoldSlug}
          hasAdditionalItems={itemCount > 1}
        />
      }
      modalSize={isSmallerThanSm ? "full" : "3xl"}
      bannerElement={<CartTimer.Banner />}
      footerDivider
      modalHeight="fit-content"
    >
      <Flex direction="column">
        {isSmallerThanSm ? (
          <VStack width="100%">
            <CDNImage
              src={selectedAttractionProduct.image ?? defaultImage}
              alt={t("Product image")}
              style={{
                borderRadius: "10px",
              }}
              width={768}
              height={768}
              responsive
            />

            {priceComponent}
            {!!selectedVariantInfo?.price && (
              <>
                <Flex width="100%" direction="column" pb="lg">
                  <Text pb="xs" fontWeight="bold">
                    {t("Select Style")}
                  </Text>
                  <ProductVariantSelect
                    isModalStyling
                    productVariants={formattedAndSortedProductVariants}
                    selectedProductVariant={selectedVariantInfo}
                    setSelectedProductVariant={setSelectedVariantInfo}
                  />
                </Flex>
                <Flex width="100%" direction="column">
                  <Text pb="xs" fontWeight="bold">
                    {t("Quantity")}
                  </Text>
                  <Flex width="100%" direction="column">
                    <Form.Incrementer
                      size="md"
                      min={purchaseLimits?.min}
                      max={
                        purchaseLimits?.max ||
                        selectedVariantInfo?.purchase_limit
                      }
                      value={amountSelectedDisplay}
                      onChange={handleAmountSelected}
                      isDisabled={isBasketUpdating}
                      isLoading={isBasketUpdating}
                    />
                  </Flex>
                </Flex>
              </>
            )}
            {readMoreComponent}
          </VStack>
        ) : (
          <Flex direction="column" height="100%" width="auto">
            {priceComponent}
            <Flex height="190px">
              <Box minWidth="190px" minHeight="190px">
                <CDNImage
                  src={selectedAttractionProduct.image ?? defaultImage}
                  alt={t("Product image")}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    borderRadius: "10px",
                  }}
                  width={190}
                  height={190}
                />
              </Box>
              <Box minWidth="element.lg" />
              {!!selectedVariantInfo?.price && (
                <Flex
                  width="100%"
                  height="190px"
                  direction="column"
                  justifyContent="space-between"
                >
                  <Flex direction="column">
                    <Text pb="xs" fontWeight="bold">
                      {t("Select Style")}
                    </Text>
                    <ProductVariantSelect
                      isModalStyling
                      productVariants={formattedAndSortedProductVariants}
                      selectedProductVariant={selectedVariantInfo}
                      setSelectedProductVariant={setSelectedVariantInfo}
                    />
                  </Flex>
                  <Flex direction="column">
                    <Text pb="xs" fontWeight="bold">
                      {t("Quantity")}
                    </Text>
                    <Flex
                      width="element.9xl"
                      direction="column"
                      data-testid={quantityButtonsDataTestId}
                    >
                      <Form.Incrementer
                        size="md"
                        min={purchaseLimits?.min}
                        max={
                          purchaseLimits?.max ||
                          selectedVariantInfo?.purchase_limit
                        }
                        value={amountSelectedDisplay}
                        onChange={handleAmountSelected}
                        isDisabled={isBasketUpdating}
                        isLoading={isBasketUpdating}
                      />
                    </Flex>
                  </Flex>
                </Flex>
              )}
            </Flex>
            {readMoreComponent}
          </Flex>
        )}
      </Flex>
    </DefaultModalContainer>
  );
}
